/*
 Title: timeZonePreference.ts
 Purpose: Client-side timezone SSOT with deterministic default.
*/

import { isValidIanaTimeZone } from "./timeCore";

const DEFAULT_TIME_ZONE = "Europe/Berlin";
const listeners = new Set<(timeZone: string) => void>();

function resolveRuntimeTimeZone(): string | null {
  try {
    const runtimeTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (runtimeTimeZone && isValidIanaTimeZone(runtimeTimeZone)) {
      return runtimeTimeZone.trim();
    }
  } catch {
    // Ignore runtime resolver errors and use deterministic fallback.
  }
  return null;
}

function resolveDefaultTimeZone(): string {
  const runtimeTimeZone = resolveRuntimeTimeZone();
  if (runtimeTimeZone) {
    return runtimeTimeZone;
  }
  if (isValidIanaTimeZone(DEFAULT_TIME_ZONE)) {
    return DEFAULT_TIME_ZONE;
  }
  return "UTC";
}

function readStoredTimeZone(): string {
  return resolveDefaultTimeZone();
}

let currentTimeZone = readStoredTimeZone();

function emit(): void {
  for (const listener of listeners) {
    try {
      listener(currentTimeZone);
    } catch {
      // listener isolation
    }
  }
}

export function getPreferredTimeZone(): string {
  return currentTimeZone;
}

export function setPreferredTimeZone(next: string): string {
  const normalized = isValidIanaTimeZone(next) ? next.trim() : resolveDefaultTimeZone();
  if (normalized === currentTimeZone) {
    return currentTimeZone;
  }
  currentTimeZone = normalized;
  emit();
  return currentTimeZone;
}

export function subscribePreferredTimeZone(listener: (timeZone: string) => void): () => void {
  listeners.add(listener);
  listener(currentTimeZone);
  return () => {
    listeners.delete(listener);
  };
}
